// ABOUTME: Resolves a family's effective bilingual label display mode — returns undefined
// ABOUTME: (not-yet-known) until resolved, then 'english' | 'english-lead' | 'bilingual'.
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from './useImpersonation';
import { familyAPI } from '../services/api';

const VALID_MODES = ['english', 'english-lead', 'bilingual'] as const;
export type LabelLanguageMode = typeof VALID_MODES[number];

function isValidMode(value: string | undefined): value is LabelLanguageMode {
  return !!value && (VALID_MODES as readonly string[]).includes(value);
}

// This hook is called from several leaf components (the tally row and all three action
// buttons), each of which can mount up to 6 simultaneous instances per child (mobile +
// desktop layouts both eager-mount). Without sharing the in-flight request, every instance
// would independently re-fetch the same family details. Cache is keyed by user+impersonation
// identity so a real account/impersonation switch still triggers a fresh fetch.
let sharedFetch: { key: string; promise: Promise<LabelLanguageMode> } | null = null;

function fetchEffectiveMode(key: string): Promise<LabelLanguageMode> {
  if (sharedFetch && sharedFetch.key === key) {
    return sharedFetch.promise;
  }

  const promise = (async (): Promise<LabelLanguageMode> => {
    try {
      const families = await familyAPI.getFamilies();
      if (families.length > 0) {
        const details = await familyAPI.getFamilyDetails(families[0].familyId);
        return isValidMode(details.effectiveLabelLanguageMode) ? details.effectiveLabelLanguageMode : 'english-lead';
      }
      return 'english-lead';
    } catch (error) {
      console.error('Failed to fetch family label language mode:', error);
      return 'english-lead';
    }
  })();

  sharedFetch = { key, promise };
  return promise;
}

// Test-only escape hatch — clears the module-level cache so each test case (which often
// reuses the same user id with different mock responses) gets a fresh fetch instead of a
// stale cached one from a prior test.
export function __resetEffectiveLabelLanguageModeCacheForTests(): void {
  sharedFetch = null;
}

export function useEffectiveLabelLanguageMode(): LabelLanguageMode | undefined {
  const { user } = useAuth();
  const { impersonatedUser, isLoading: impersonationLoading } = useImpersonation();
  const [mode, setMode] = useState<LabelLanguageMode | undefined>(undefined);

  useEffect(() => {
    if (!user || impersonationLoading) return;

    let cancelled = false;
    const resolveMode = async () => {
      // Reset to undefined (blocking) on every re-fetch — e.g. when impersonation changes —
      // so a stale mode from the previous family/user never renders during the refetch window.
      setMode(undefined);

      const key = `${user.userId}:${impersonatedUser?.userId ?? ''}`;
      const resolved = await fetchEffectiveMode(key);
      if (!cancelled) {
        setMode(resolved);
      }
    };

    resolveMode();
    return () => { cancelled = true; };
  }, [user, impersonatedUser?.userId, impersonationLoading]);

  if (!user) return undefined;
  if (impersonationLoading) return undefined;

  return mode;
}
